import React, { useState } from 'react';
import {
  Clock,
  FileText,
  AlertCircle,
  Eye,
  CheckCircle,
  DollarSign,
  TrendingUp,
  Users,
  Building2,
  GripVertical,
  User,
  Calendar,
} from 'lucide-react';
import { AlertBadge } from '../shared/AlertBanner';

// Status configuration (Blocked removed - critical issues shown in separate section)
const STATUS_CONFIG = {
  not_started: {
    label: 'Not Started',
    color: 'bg-boss-elevated',
    textColor: 'text-boss-text',
    icon: Clock,
  },
  in_progress: {
    label: 'In Progress',
    color: 'bg-blue-500',
    textColor: 'text-blue-100',
    icon: FileText,
  },
  ready_for_review: {
    label: 'Ready for Review',
    color: 'bg-yellow-500',
    textColor: 'text-yellow-100',
    icon: Eye,
  },
  complete: {
    label: 'Complete',
    color: 'bg-green-500',
    textColor: 'text-green-100',
    icon: CheckCircle,
  },
};

const STATUS_ORDER = ['not_started', 'in_progress', 'ready_for_review', 'complete'];

const LOB_COLORS = {
  field_services: 'bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30',
  professional_services: 'bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30',
  wholesale_mrr: 'bg-green-50 dark:bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30',
  referral_mrr: 'bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
};

const LOB_LABELS = {
  field_services: 'Field Services',
  professional_services: 'Professional Services',
  wholesale_mrr: 'Wholesale MRR',
  referral_mrr: 'Referral MRR',
};

// Parse a 'YYYY-MM-DD' / ISO date string into a local date pinned to midnight,
// so comparisons are day-granular and not skewed by time-of-day or timezone.
const parseDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Evaluate the date-risk state of a SOW for the Kanban "at risk" alert (2.2).
// Precedence: a missed (past) date outranks a mere expected-after-desired
// mismatch. Completed SOWs are never flagged. Returns a level + human-readable
// reason used for both the blinking badge and the expected-date row colour.
export const getDateRiskState = (sow) => {
  if (!sow || sow.status === 'complete') return { level: null };

  const today = startOfToday();
  const expected = parseDateOnly(sow.expected_completion_date);
  const desired = parseDateOnly(sow.desired_proposal_date);

  // 1. OVERDUE — keyed ONLY off the expected completion date (the sales-ops
  // commitment). The desired proposal date is sales' field and must not drive
  // an overdue flag (Ryan, 18 Jun); using it caused false "overdue" on SOWs
  // where no expected date had been set. No expected date → no overdue.
  if (expected && expected < today) {
    return { level: 'overdue', label: 'OVERDUE', reason: 'Expected completion date has passed' };
  }

  // 2. AT RISK — sales ops expects to finish after sales wants the proposal.
  if (expected && desired && expected > desired) {
    return {
      level: 'at_risk',
      label: 'AT RISK',
      reason: 'Expected completion is after the desired proposal date',
    };
  }

  return { level: null };
};

export default function SolutionsDesignKanban({
  sowTasks,
  salesOpsUsers = [],
  opportunityAlerts = {},
  onStatusChange,
  onViewSOW,
  onAssignSOW,
}) {
  const [draggedSOW, setDraggedSOW] = useState(null);

  // Critical vs normal must be a strict PARTITION of sowTasks — every SOW lands
  // in exactly one bucket, or it gets counted ("N SOWs") yet rendered nowhere.
  // A SOW is treated as critical (→ Critical Issues section) if it has critical
  // blockers, a critical opportunity alert, OR its stored status is 'blocked'.
  // The 'blocked' case matters: the Blocked column was removed in favour of the
  // Critical Issues section, but a SOW can sit at status='blocked' without a
  // live critical flag (e.g. once an overdue/at-risk date is reclassified) — and
  // such SOWs were previously dropped from both the columns and the section.
  const isCriticalSOW = (sow) => {
    const alerts = opportunityAlerts[sow.opportunity_id] || [];
    const hasCriticalAlerts = alerts.some((a) => a.severity === 'critical' && !a.dismissed);
    return sow.has_critical_blockers === true || hasCriticalAlerts || sow.status === 'blocked';
  };

  const criticalSOWs = sowTasks.filter(isCriticalSOW);
  const normalSOWs = sowTasks.filter((sow) => !isCriticalSOW(sow));

  const handleDragStart = (e, sow) => {
    // SOWs with critical flags are now draggable (Ryan, 18 Jun). The Critical
    // Issues banner still highlights them prominently, but a flag must not trap
    // the SOW — the team needs to move it forward and address the issue later.
    setDraggedSOW(sow);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    if (!draggedSOW || draggedSOW.status === targetStatus) {
      setDraggedSOW(null);
      return;
    }

    await onStatusChange(draggedSOW.sow_id, targetStatus);
    setDraggedSOW(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const renderSOWCard = (sow, isCritical = false) => {
    // Calculate blocker info from resource assignments (if available)
    const hasBlockers = sow.has_critical_blockers === true || sow.status === 'blocked';
    const resourceCount = (sow.selected_resource_assignment_ids || []).length;

    // Date-risk evaluation (Ryan, 15 Jun — Kanban "at risk" alert, 2.2).
    // Two triggers, in precedence order:
    //   1. OVERDUE  — a relevant date has already passed and the SOW isn't
    //                 complete ("also flag already-missed dates").
    //   2. AT RISK  — expected completion is later than the desired proposal
    //                 date (sales-ops slower than sales wants), but nothing
    //                 has actually slipped past today yet.
    // Returns { level: 'overdue' | 'at_risk' | null, label, reason }.
    const dateRisk = getDateRiskState(sow);

    // Get alerts for this opportunity
    const alerts = opportunityAlerts[sow.opportunity_id] || [];
    const criticalAlerts = alerts.filter((a) => a.severity === 'critical' && !a.dismissed);
    const warningAlerts = alerts.filter((a) => a.severity === 'warning' && !a.dismissed);
    const successAlerts = alerts.filter((a) => a.severity === 'success' && !a.dismissed);

    // Prepare blocker text array (for critical section display)
    const blockerTexts = sow.blockers && Array.isArray(sow.blockers) ? sow.blockers : [];

    // Show details if in critical section
    const showCriticalDetails = isCritical;

    return (
      <div
        key={sow.sow_id}
        draggable
        onDragStart={(e) => handleDragStart(e, sow)}
        className={`border rounded-lg p-4 transition-all group cursor-move ${
          isCritical
            ? 'boss-alert-danger shadow-lg'
            : 'bg-boss-surface border-boss-border hover:border-boss-border-active'
        } ${isCritical ? '' : 'mb-3'}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span
                className={`px-2 py-1 rounded text-xs font-medium border ${LOB_COLORS[sow.line_of_business]}`}
              >
                {LOB_LABELS[sow.line_of_business]}
              </span>

              {/* Alert Badges (NEW) */}
              {criticalAlerts.length > 0 && (
                <AlertBadge severity="critical" count={criticalAlerts.length} />
              )}
              {warningAlerts.length > 0 && (
                <AlertBadge severity="warning" count={warningAlerts.length} />
              )}
              {successAlerts.length > 0 && (
                <AlertBadge severity="success" count={successAlerts.length} />
              )}

              {/* Date-risk badge (domain-specific warning, blinking — 2.2).
                  Overdue (a missed date) is more severe than at-risk and is
                  rendered red; at-risk stays yellow. Both blink to catch the
                  eye on Jess's escalation board. */}
              {dateRisk.level && (
                <span
                  className={`boss-chip animate-risk-blink ${
                    dateRisk.level === 'overdue' ? 'boss-chip-danger' : 'boss-chip-warning'
                  }`}
                  title={dateRisk.reason}
                >
                  {dateRisk.level === 'overdue' ? (
                    <AlertCircle className="w-3 h-3" />
                  ) : (
                    <Calendar className="w-3 h-3" />
                  )}
                  {dateRisk.label}
                </span>
              )}
            </div>
            <p className="text-sm text-boss-text-secondary font-medium truncate">
              {sow.opportunity_name || 'SOW'}
            </p>
          </div>
          {!isCritical && (
            <GripVertical className="w-4 h-4 text-boss-text-muted opacity-0 group-hover:opacity-100 flex-shrink-0" />
          )}
        </div>

        {/* Assignment Dropdown */}
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <User className="w-3 h-3 text-boss-text-secondary flex-shrink-0" />
            <select
              value={sow.assigned_to || ''}
              onChange={(e) => onAssignSOW && onAssignSOW(sow.sow_id, e.target.value)}
              onClick={(e) => e.stopPropagation()} // Prevent card drag when clicking dropdown
              className="flex-1 px-2 py-1 bg-boss-surface border border-boss-border rounded text-boss-text text-xs focus:outline-none focus:border-violet-500"
            >
              <option value="">Assign to...</option>
              {salesOpsUsers.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-boss-text-secondary flex items-center gap-1">
              <Users className="w-3 h-3" />
              Resources
            </span>
            <span className="text-boss-text font-medium">{resourceCount}</span>
          </div>

          {sow.total_amount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-boss-text-secondary flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Amount
              </span>
              <span className="text-green-600 dark:text-green-400 font-semibold">
                {formatCurrency(sow.total_amount)}
              </span>
            </div>
          )}

          {/* Expected Completion Date */}
          {sow.expected_completion_date && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-boss-text-muted flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Expected
              </span>
              <span
                className="font-semibold"
                style={{
                  color:
                    dateRisk.level === 'overdue'
                      ? 'var(--boss-danger-text)'
                      : dateRisk.level === 'at_risk'
                        ? 'var(--boss-warning-text)'
                        : 'var(--boss-text-secondary)',
                  fontWeight: dateRisk.level ? 600 : 400,
                }}
              >
                {new Date(sow.expected_completion_date).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Critical Details (Blockers or Alert Messages) */}
        {showCriticalDetails && (
          <div
            className="mb-3 p-2 rounded border"
            style={{
              background: 'var(--boss-danger-soft)',
              borderColor: 'var(--boss-danger-border)',
            }}
          >
            {blockerTexts.length > 0 ? (
              <>
                {blockerTexts.slice(0, 2).map((blocker, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-1 text-xs mb-1 last:mb-0"
                    style={{ color: 'var(--boss-danger-text)' }}
                  >
                    <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-1">{blocker}</span>
                  </div>
                ))}
                {blockerTexts.length > 2 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--boss-danger-text)' }}>+{blockerTexts.length - 2} more...</p>
                )}
              </>
            ) : criticalAlerts.length > 0 ? (
              <>
                {criticalAlerts.slice(0, 2).map((alert, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-1 text-xs mb-1 last:mb-0"
                    style={{ color: 'var(--boss-danger-text)' }}
                  >
                    <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-1">
                      {alert.rule_name}: {alert.message}
                    </span>
                  </div>
                ))}
                {criticalAlerts.length > 2 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--boss-danger-text)' }}>+{criticalAlerts.length - 2} more...</p>
                )}
              </>
            ) : (
              <div className="flex items-start gap-1 text-xs" style={{ color: 'var(--boss-danger-text)' }}>
                <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-1">Critical issue - view SOW for details</span>
              </div>
            )}
          </div>
        )}

        {/* View Button */}
        <button
          onClick={() => onViewSOW(sow)}
          className="w-full py-2 bg-boss-accent hover:bg-boss-accent-hover text-white text-sm rounded-lg transition flex items-center justify-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View SOW
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Critical Issues Section (Hybrid Approach - Option C) */}
      {criticalSOWs.length > 0 && (
        <div className="boss-alert-danger rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="p-2 rounded-lg"
              style={{ background: 'var(--boss-danger-soft)' }}
            >
              <AlertCircle
                className="w-6 h-6 animate-pulse"
                style={{ color: 'var(--boss-danger-text)' }}
              />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ color: 'var(--boss-danger-text)' }}>
                🚨 Critical Issues Requiring Attention
              </h3>
              <p className="text-sm opacity-90" style={{ color: 'var(--boss-danger-text)' }}>
                {criticalSOWs.length} SOW{criticalSOWs.length > 1 ? 's' : ''} with blockers or
                critical alerts — resolve issues to unblock
              </p>
            </div>
          </div>

          {/* Critical SOW Cards in horizontal scroll */}
          <div className="flex gap-4 overflow-x-auto pb-2">
            {criticalSOWs.map((sow) => (
              <div key={sow.sow_id} className="min-w-[340px]">
                {renderSOWCard(sow, true)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regular Kanban Board (4 columns - Blocked removed) */}
      <div className="overflow-x-auto -mx-6 px-6">
        <div className="flex gap-4 pb-4 min-w-min">
          {STATUS_ORDER.map((status) => {
            const config = STATUS_CONFIG[status];
            const StatusIcon = config.icon;
            // Place each normal SOW in its column; any SOW whose stored status
            // isn't one of the four workflow columns (e.g. null/legacy/unknown)
            // falls back to Not Started so it's always visible rather than
            // silently dropped while still counting toward the header total.
            const sowsInStatus = normalSOWs.filter((s) =>
              (STATUS_ORDER.includes(s.status) ? s.status : 'not_started') === status,
            );
            const totalAmount = sowsInStatus.reduce((sum, s) => sum + (s.total_amount || 0), 0);

            return (
              <div
                key={status}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status)}
                className="flex-shrink-0 w-80 bg-boss-base rounded-lg border border-boss-border"
              >
                {/* Column Header */}
                <div className={`${config.color} ${config.textColor} p-3 rounded-t-lg`}>
                  <div className="flex items-center gap-2 mb-1">
                    <StatusIcon className="w-4 h-4" />
                    <h3 className="font-semibold text-sm">{config.label}</h3>
                  </div>
                  <div className="flex items-center justify-between text-xs opacity-90">
                    <span>
                      {sowsInStatus.length} SOW{sowsInStatus.length !== 1 ? 's' : ''}
                    </span>
                    {totalAmount > 0 && <span>{formatCurrency(totalAmount)}</span>}
                  </div>
                </div>

                {/* Cards */}
                <div className="p-3 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {sowsInStatus.length === 0 ? (
                    <div className="text-center py-8 text-boss-text-muted text-sm">No SOWs</div>
                  ) : (
                    sowsInStatus.map((sow) => renderSOWCard(sow, false))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
